import { createHash } from 'crypto';
import fs from 'fs/promises';
import path from 'path';

import { SyncHttpClient } from '@coderule/clients';
import type { Logger } from 'pino';

import type { FilesRepo } from '../db/FilesRepo';
import type { SnapshotsRepo } from '../db/SnapshotsRepo';
import type { InitialSyncResult, SnapshotComputation } from '../types';

async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export class SnapshotStaleError extends Error {
  constructor(message = 'snapshot became stale') {
    super(message);
    this.name = 'SnapshotStaleError';
  }
}

export interface PublishOptions {
  maxAttempts?: number;
  uploadChunkSize?: number;
  deadlineMs?: number;
  isStale?: () => boolean;
}

function assertNotStale(opts?: PublishOptions): void {
  if (opts?.isStale?.()) {
    throw new SnapshotStaleError();
  }
}

function assertBeforeDeadline(opts?: PublishOptions): void {
  if (opts?.deadlineMs !== undefined && Date.now() >= opts.deadlineMs) {
    throw new Error('snapshot publish deadline exceeded');
  }
}

function assertValid(opts?: PublishOptions): void {
  assertNotStale(opts);
  assertBeforeDeadline(opts);
}

function withDeadline<T>(
  promise: Promise<T>,
  opts?: PublishOptions,
): Promise<T> {
  if (opts?.deadlineMs === undefined) return promise;
  const remaining = opts.deadlineMs - Date.now();
  if (remaining <= 0) {
    return Promise.reject(new Error('snapshot publish deadline exceeded'));
  }
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(
        () => reject(new Error('snapshot publish deadline exceeded')),
        remaining,
      ),
    ),
  ]);
}

export function computeSnapshot(filesRepo: FilesRepo): SnapshotComputation {
  const files = filesRepo.getCleanFilesForSnapshot();
  const hashes = files
    .map((file) => file.service_file_hash)
    .filter((hash): hash is string => typeof hash === 'string');
  const snapshotHash = SyncHttpClient.calculateSnapshotHash([...hashes].sort());
  const { filesCount, totalSize } = filesRepo.getTotalsForSnapshot();
  return {
    snapshotHash,
    files: files.map((file) => ({
      file_path: file.rel_path,
      file_hash: file.service_file_hash,
    })),
    filesCount,
    totalSize,
  };
}

async function withRetries<T>(
  op: () => Promise<T>,
  logger: Logger,
  context: Record<string, unknown>,
  maxAttempts: number,
  opts?: PublishOptions,
): Promise<T> {
  let attempt = 0;
  // First attempt + (maxAttempts - 1) retries
  while (true) {
    assertValid(opts);
    try {
      return await withDeadline(op(), opts);
    } catch (err: any) {
      // Let staleness and deadline errors propagate immediately
      if (err instanceof SnapshotStaleError) throw err;
      if (
        typeof err?.message === 'string' &&
        err.message.includes('deadline exceeded')
      ) {
        throw err;
      }
      attempt += 1;
      if (attempt >= maxAttempts) {
        logger.error(
          { err, ...context, attempt },
          'Operation failed after retries',
        );
        throw err;
      }
      const delay = Math.min(15_000, 1_000 * 2 ** (attempt - 1));
      logger.warn(
        { err, ...context, attempt, delay },
        'Operation failed; retrying',
      );
      await sleep(delay);
    }
  }
}

function computeServiceFileHash(relPath: string, content: Buffer): string {
  const hash = createHash('sha256');
  hash.update(relPath);
  hash.update('\n');
  hash.update(content);
  return hash.digest('hex');
}

async function uploadMissing(
  rootPath: string,
  missing: Array<{ file_path: string; file_hash: string }> | undefined,
  syncClient: SyncHttpClient,
  logger: Logger,
  maxAttempts: number,
  chunkSize = 1,
  opts?: PublishOptions,
): Promise<void> {
  if (!missing || missing.length === 0) return;

  const total = missing.length;
  const chunks: Array<Array<{ file_path: string; file_hash: string }>> = [];
  for (let i = 0; i < total; i += chunkSize) {
    chunks.push(missing.slice(i, i + chunkSize));
  }

  for (let idx = 0; idx < chunks.length; idx++) {
    assertValid(opts);

    const list = chunks[idx]!;
    const map = new Map<string, { path: string; content: Buffer }>();
    for (const missingFile of list) {
      const absPath = path.join(rootPath, missingFile.file_path);
      try {
        const buffer = await fs.readFile(absPath);
        const serviceHash = computeServiceFileHash(
          missingFile.file_path,
          buffer,
        );
        if (serviceHash !== missingFile.file_hash) {
          logger.warn(
            {
              relPath: missingFile.file_path,
              expected: missingFile.file_hash,
              got: serviceHash,
            },
            'File content changed during upload; snapshot is stale',
          );
          throw new SnapshotStaleError(
            `file hash mismatch for ${missingFile.file_path}`,
          );
        }
        map.set(missingFile.file_hash, {
          path: missingFile.file_path,
          content: buffer,
        });
      } catch (error: any) {
        if (error instanceof SnapshotStaleError) throw error;
        logger.warn(
          { err: error, relPath: missingFile.file_path },
          'Failed to read missing file content',
        );
      }
    }
    if (map.size === 0) continue;

    assertValid(opts);

    await withRetries(
      () => syncClient.uploadFileContent(map),
      logger,
      {
        op: 'uploadFileContent',
        chunkIndex: idx + 1,
        chunks: chunks.length,
        files: map.size,
      },
      maxAttempts,
      opts,
    );
  }
}

async function ensureSnapshotCreated(
  rootPath: string,
  computation: SnapshotComputation,
  syncClient: SyncHttpClient,
  logger: Logger,
  opts?: PublishOptions,
): Promise<void> {
  const { snapshotHash, files } = computation;
  const maxAttempts = opts?.maxAttempts ?? 5;
  const uploadChunkSize = opts?.uploadChunkSize ?? 1;

  assertValid(opts);

  let status = await withRetries(
    () => syncClient.checkSnapshotStatus(snapshotHash),
    logger,
    { op: 'checkSnapshotStatus', snapshotHash },
    maxAttempts,
    opts,
  );
  if (status.status === 'READY') {
    logger.info({ snapshotHash }, 'Snapshot already READY');
    return;
  }

  let attempt = 0;
  while (status.status !== 'READY') {
    assertValid(opts);

    if (status.status === 'FAILED') {
      throw new Error(`Snapshot failed processing: ${JSON.stringify(status)}`);
    }

    if (status.status === 'NOT_FOUND' || status.status === 'MISSING_CONTENT') {
      status = await withRetries(
        () => syncClient.createSnapshot(snapshotHash, files),
        logger,
        { op: 'createSnapshot', snapshotHash },
        maxAttempts,
        opts,
      );
    }

    if (status.status === 'MISSING_CONTENT' && status.missing_files?.length) {
      logger.info(
        { missing: status.missing_files.length },
        'Uploading missing file content',
      );
      await uploadMissing(
        rootPath,
        status.missing_files,
        syncClient,
        logger,
        maxAttempts,
        uploadChunkSize,
        opts,
      );
      status = await withRetries(
        () => syncClient.createSnapshot(snapshotHash, files),
        logger,
        { op: 'createSnapshot', snapshotHash },
        maxAttempts,
        opts,
      );
      continue;
    }

    if (status.status === 'READY') break;

    // PROCESSING or other non-terminal statuses: backoff and poll
    const delay = Math.min(5_000, 1_000 * Math.max(1, 2 ** attempt));
    await sleep(delay);
    attempt += 1;

    assertValid(opts);

    status = await withRetries(
      () => syncClient.checkSnapshotStatus(snapshotHash),
      logger,
      { op: 'checkSnapshotStatus', snapshotHash },
      maxAttempts,
      opts,
    );
  }

  logger.info({ snapshotHash }, 'Snapshot READY');
}

export async function publishSnapshot(
  rootPath: string,
  filesRepo: FilesRepo,
  snapshotsRepo: SnapshotsRepo,
  syncClient: SyncHttpClient,
  logger: Logger,
  options?: PublishOptions,
): Promise<InitialSyncResult> {
  const computation = computeSnapshot(filesRepo);
  await ensureSnapshotCreated(
    rootPath,
    computation,
    syncClient,
    logger,
    options,
  );
  const createdAt = Date.now();
  snapshotsRepo.insert(
    computation.snapshotHash,
    computation.filesCount,
    computation.totalSize,
    createdAt,
  );
  return {
    snapshotHash: computation.snapshotHash,
    filesCount: computation.filesCount,
    totalSize: computation.totalSize,
    status: 'READY',
    createdAt,
  };
}
